import { Actions, createEffect, ofType } from '@ngrx/effects'
import { SNACKBAR_LENGTH_LONG, SnackbarEventType, SnackbarService } from '@shared/utils/snackbar.service'
import { catchError, concatMap, filter, map, mergeMap, of, switchMap, withLatestFrom } from 'rxjs'

import { Injectable } from '@angular/core'
import { ScopeTabService } from '@app/features/scope-overview/service/scope-tab.service'
import { Store } from '@ngrx/store'
import { DELIVERABLES_PAGE_SIZE, ScopePaginatedService } from '../../service/scope-paginated.service'
import { mapScopeVersion } from '../../utils/map-scope-version.utils'
import { retryUntilUpdated } from '../../utils/retry-until-updated.operator'
import { ScopeOverviewActions, ScopeOverviewActionTypes } from '../actions/scope-overview.action'
import {
  ScopePaginatedOverviewActions,
  ScopePaginatedOverviewActionTypes,
} from '../actions/scope-paginated-overview.actions'
import { ScopeOverviewSelectors } from '../selectors/scope-overview.selector'
import { mapScopeDeliverable } from '../../utils/map-scope-deliverable.utils'
import { ScopeOverviewService } from '../../service/scope-overview.service'
import { Deliverable } from '@app/features/scoping/models/deliverable.model'
import { LanguageService } from '@app/core/service/language.service'
import { ScopeConfigurationActions } from '../actions/scope-configuration.actions'

@Injectable()
export class ScopePaginatedOverviewEffects {
  constructor(
    private store$: Store,
    private actions$: Actions,
    private lang: LanguageService,
    private scopePaginatedService: ScopePaginatedService,
    private scopeOverviewService: ScopeOverviewService,
    private snackbarService: SnackbarService,
    private scopeTabService: ScopeTabService
  ) {}

  getCurrentScope$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScopePaginatedOverviewActions.getCurrentScope),
      switchMap((action) => {
        this.scopeTabService.resetDeliverablesState()
        return this.scopePaginatedService.getScope(action.scopeId).pipe(
          map((currentScope) =>
            ScopePaginatedOverviewActions.getCurrentScopeSuccess({
              currentScope: mapScopeVersion(currentScope),
            })
          ),
          catchError((error) => {
            this.snackbarService.showSnackbar(
              `An error occurred during loading of current Scope`,
              SNACKBAR_LENGTH_LONG,
              SnackbarEventType.ERROR
            )
            return of(ScopePaginatedOverviewActions.getCurrentScopeFail({ error }))
          })
        )
      })
    )
  )

  getCurrentScopeValues$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScopePaginatedOverviewActions.getCurrentScopeValues),
      withLatestFrom(this.store$.select(ScopeOverviewSelectors.selectCurrentScope)),
      concatMap(([{ scopeId }, currentScope]) =>
        this.scopePaginatedService.getScope(scopeId).pipe(
          retryUntilUpdated({
            isUpdated: (response) => currentScope?.calculatedValues?.updatedOn !== response.calculatedValues?.updatedOn,
            retryObservable: () => this.scopePaginatedService.getScope(scopeId),
          }),
          map((response) => ScopePaginatedOverviewActions.getCurrentScopeValuesSuccess({ currentScope: response })),
          catchError((error) => of(ScopePaginatedOverviewActions.getCurrentScopeValuesFail({ error })))
        )
      )
    )
  )

  getCurrentScopeSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScopePaginatedOverviewActions.getCurrentScopeSuccess),
      switchMap(() => [
        ScopePaginatedOverviewActions.getScopeDeliverables(),
        ScopePaginatedOverviewActions.getScopeSections(),
      ])
    )
  )

  getScopeSections$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScopePaginatedOverviewActions.getScopeSections),
      withLatestFrom(
        this.store$.select(ScopeOverviewSelectors.selectCurrentScope),
        this.store$.select(ScopeOverviewSelectors.selectSectionsPagination)
      ),
      filter(([, currentScope, pagination]) => currentScope && !(pagination.loading || pagination.last)),
      map(([, currentScope]) =>
        ScopePaginatedOverviewActions.requestScopeSections({ scopeId: currentScope.identity.id })
      )
    )
  )

  requestScopeSections$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScopePaginatedOverviewActions.requestScopeSections),
      withLatestFrom(this.store$.select(ScopeOverviewSelectors.selectSectionsPagination)),
      switchMap(([{ scopeId }, pagination]) =>
        this.scopePaginatedService.getSections(scopeId, { page: pagination.number + 1 }).pipe(
          map((response) => ScopePaginatedOverviewActions.getScopeSectionsSuccess({ response })),
          catchError((error) => {
            console.info(error)
            this.snackbarService.showSnackbar(
              `An error occurred during loading of current Scope sections`,
              SNACKBAR_LENGTH_LONG,
              SnackbarEventType.ERROR
            )
            return of(ScopePaginatedOverviewActions.getScopeSectionsFail({ error }))
          })
        )
      )
    )
  )

  private readonly PREFETCH_BUFFER_SIZE = 3

  getScopeDeliverables$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScopePaginatedOverviewActions.getScopeDeliverables),
      withLatestFrom(
        this.store$.select(ScopeOverviewSelectors.selectCurrentScope),
        this.store$.select(ScopeOverviewSelectors.selectDeliverablesPagination),
        this.store$.select(ScopeOverviewSelectors.selectBufferedDeliverablesCount)
      ),
      filter(
        ([, currentScope, pagination, bufferedCount]) =>
          currentScope &&
          !pagination.loading &&
          !pagination.last &&
          bufferedCount < this.PREFETCH_BUFFER_SIZE * DELIVERABLES_PAGE_SIZE
      ),
      map(([, currentScope]) =>
        ScopePaginatedOverviewActions.requestScopeDeliverables({ scopeId: currentScope.identity.id })
      )
    )
  )

  requestScopeDeliverables$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScopePaginatedOverviewActions.requestScopeDeliverables),
      withLatestFrom(this.store$.select(ScopeOverviewSelectors.selectDeliverablesPagination)),
      switchMap(([{ scopeId }, pagination]) =>
        this.scopePaginatedService.getDeliverables(scopeId, { page: pagination.number + 1 }).pipe(
          map((response) => ScopePaginatedOverviewActions.getScopeDeliverablesSuccess({ response })),
          catchError((error) => {
            this.snackbarService.showSnackbar(
              `An error occurred during loading of current Scope deliverables`,
              SNACKBAR_LENGTH_LONG,
              SnackbarEventType.ERROR
            )
            return of(ScopePaginatedOverviewActions.getScopeDeliverablesFail({ error }))
          })
        )
      )
    )
  )

  prefetchDeliverables$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ScopePaginatedOverviewActions.getScopeDeliverablesSuccess,
        ScopePaginatedOverviewActions.showNextBufferedDeliverables
      ),
      map(() => ScopePaginatedOverviewActions.getScopeDeliverables())
    )
  )

  getScopeSectionDeliverables$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScopePaginatedOverviewActions.getScopeSectionDeliverables),
      withLatestFrom(
        this.store$.select(ScopeOverviewSelectors.selectCurrentScope),
        this.store$.select(ScopeOverviewSelectors.selectSectionDeliverablesPagination)
      ),
      filter(([{ sectionId }, currentScope, pagination]) => {
        const sectionPagination = pagination[sectionId]
        return sectionId && currentScope && !(sectionPagination?.loading || sectionPagination?.last)
      }),
      map(([{ sectionId }, currentScope]) =>
        ScopePaginatedOverviewActions.requestScopeSectionDeliverables({ scopeId: currentScope.identity.id, sectionId })
      )
    )
  )

  requestScopeSectionDeliverables$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScopePaginatedOverviewActions.requestScopeSectionDeliverables),
      withLatestFrom(this.store$.select(ScopeOverviewSelectors.selectSectionDeliverablesPagination)),
      switchMap(([{ scopeId, sectionId }, pagination]) => {
        const sectionPagination = pagination[sectionId]
        const page = sectionPagination ? sectionPagination.number + 1 : 0
        return this.scopePaginatedService.getSectionDeliverables(scopeId, sectionId, { page }).pipe(
          map((response) => ScopePaginatedOverviewActions.getScopeSectionDeliverablesSuccess({ sectionId, response })),
          catchError((error) => {
            console.info(error)
            this.snackbarService.showSnackbar(
              `An error occurred during loading of Scope section deliverables`,
              SNACKBAR_LENGTH_LONG,
              SnackbarEventType.ERROR
            )
            return of(ScopePaginatedOverviewActions.getScopeSectionDeliverablesFail({ sectionId, error }))
          })
        )
      })
    )
  )

  getScopeDeliverable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScopePaginatedOverviewActions.getScopeDeliverable),
      mergeMap(({ scopeId, deliverableId }) => {
        return this.scopePaginatedService.getScopeDeliverable(scopeId, deliverableId).pipe(
          map((response) =>
            ScopePaginatedOverviewActions.getScopeDeliverableSuccess({ deliverable: mapScopeDeliverable(response) })
          ),
          catchError((error) => {
            this.snackbarService.showSnackbar(
              `An error occurred during loading the deliverable`,
              SNACKBAR_LENGTH_LONG,
              SnackbarEventType.ERROR
            )
            return of(ScopePaginatedOverviewActions.getScopeDeliverableFail({ error, deliverableId }))
          })
        )
      })
    )
  )

  getUpdatedDeliverable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScopePaginatedOverviewActions.getUpdatedDeliverable),
      withLatestFrom(this.store$.select(ScopeOverviewSelectors.selectCurrentScope)),
      map(([{ scopeId, deliverableId }, currentScope]) => {
        const deliverable = currentScope.deliverables.find((d) => d.id === deliverableId)
        return { scopeId, deliverableId, deliverable }
      }),
      mergeMap(({ scopeId, deliverableId, deliverable }) =>
        this.scopePaginatedService.getScopeDeliverable(scopeId, deliverableId).pipe(
          retryUntilUpdated({
            isUpdated: (response) => deliverable.calculatedValues?.updatedOn !== response.calculatedValues?.updatedOn,
            retryObservable: () => this.scopePaginatedService.getScopeDeliverable(scopeId, deliverableId),
          }),
          map((response) =>
            ScopePaginatedOverviewActions.getScopeDeliverableSuccess({ deliverable: response, isUpdated: true })
          ),
          catchError((error) => {
            this.snackbarService.showSnackbar(
              `An error occurred during loading the deliverable`,
              SNACKBAR_LENGTH_LONG,
              SnackbarEventType.ERROR
            )
            return of(ScopePaginatedOverviewActions.getScopeDeliverableFail({ error, deliverableId }))
          })
        )
      )
    )
  )

  triggerGetUpdatedDeliverable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ScopeOverviewActions.updateScopeDeliverableSuccess,
        ScopeOverviewActions.updateDeliverableTpcSuccess,
        ScopeOverviewActions.deleteDeliverableFeeItemSuccess,
        ScopeOverviewActions.createDeliverableFeeItemSuccess,
        ScopeOverviewActions.editDeliverableFeeItemSuccess,

        ScopeOverviewActions.updateDeliverableSectionSuccess,
        ScopeOverviewActions.updateDeliverableSectionTpcSuccess,

        ScopeOverviewActions.deleteComponentSuccess,
        ScopeOverviewActions.updateScopeComponentSuccess,
        ScopeOverviewActions.updateComponentTpcSuccess,
        ScopeOverviewActions.editComponentFeeItemSuccess,
        ScopeOverviewActions.createComponentFeeItemSuccess,
        ScopeOverviewActions.deleteComponentFeeSuccess,

        ScopeOverviewActions.overrideRoleSuccess
      ),
      withLatestFrom(this.store$.select(ScopeOverviewSelectors.selectCurrentScope)),
      map(([action, currentScope]) => {
        let deliverableId: number

        switch (action.type) {
          case ScopeOverviewActionTypes.UPDATE_SCOPE_DELIVERABLE_SUCCESS:
          case ScopeOverviewActionTypes.UPDATE_DELIVERABLE_TPC_SUCCESS:
            deliverableId = action.deliverable.id
            break
          case ScopeOverviewActionTypes.UPDATE_COMPONENT_TPC_SUCCESS:
          case ScopeOverviewActionTypes.OVERRIDE_ROLE_SUCCESS:
          case ScopeOverviewActionTypes.UPDATE_SCOPE_COMPONENT_SUCCESS:
            deliverableId = action.component.deliverable.id
            break
          case ScopeOverviewActionTypes.EDIT_DELIVERABLE_FEE_ITEM_SUCCESS:
          case ScopeOverviewActionTypes.DELETE_DELIVERABLE_FEE_ITEM_SUCCESS:
          case ScopeOverviewActionTypes.CREATE_DELIVERABLE_FEE_ITEM_SUCCESS:
          case ScopeOverviewActionTypes.EDIT_COMPONENT_FEE_ITEM_SUCCESS:
          case ScopeOverviewActionTypes.DELETE_COMPONENT_FEE_SUCCESS:
          case ScopeOverviewActionTypes.CREATE_COMPONENT_FEE_ITEM_SUCCESS:
          case ScopeOverviewActionTypes.DELETE_COMPONENT_SUCCESS:
            deliverableId = action.deliverableId
            break
        }

        return { scopeId: currentScope.identity.id, deliverableId }
      }),
      filter(({ deliverableId }) => !!deliverableId),
      map(({ scopeId, deliverableId }) =>
        ScopePaginatedOverviewActions.getUpdatedDeliverable({ scopeId, deliverableId })
      )
    )
  )

  moveDeliverable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScopePaginatedOverviewActions.moveDeliverable),
      withLatestFrom(this.store$.select(ScopeOverviewSelectors.selectCurrentScope)),
      mergeMap(([{ fromSectionId, deliverable }, currentScope]) =>
        this.scopeOverviewService.updateScopeDeliverable(currentScope.identity.id, deliverable).pipe(
          switchMap((updatedDeliverable: Deliverable) => [
            ScopeOverviewActions.updateScopeDeliverableSuccess({ deliverable: updatedDeliverable }),
            ScopePaginatedOverviewActions.moveDeliverableSuccess({ fromSectionId, deliverable: updatedDeliverable }),
          ]),
          catchError((error: string) => {
            this.snackbarService.showSnackbar(
              `An error occurred during ${this.lang.get('deliverable|l')} update`,
              SNACKBAR_LENGTH_LONG,
              SnackbarEventType.ERROR
            )
            return of(ScopePaginatedOverviewActions.moveDeliverableFail({ fromSectionId, deliverable, error }))
          })
        )
      )
    )
  )

  triggerGetUpdatedSection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ScopeOverviewActions.updateScopeDeliverableSuccess,
        ScopeOverviewActions.createSectionFeeItemSuccess,
        ScopeOverviewActions.editSectionFeeItemSuccess,
        ScopeOverviewActions.deleteSectionFeeItemSuccess,
        ScopePaginatedOverviewActions.moveDeliverableSuccess
      ),
      mergeMap((action) => {
        let sectionId: number

        switch (action.type) {
          case ScopeOverviewActionTypes.UPDATE_SCOPE_DELIVERABLE_SUCCESS:
            sectionId = action.deliverable?.section?.id
            break
          case ScopePaginatedOverviewActionTypes.MOVE_DELIVERABLE_SUCCESS:
            sectionId = action.fromSectionId
            break
          default:
            sectionId = action.sectionId
            break
        }

        return [ScopePaginatedOverviewActions.getScopeSection({ sectionId })]
      })
    )
  )

  getScopeSection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScopePaginatedOverviewActions.getScopeSection),
      withLatestFrom(this.store$.select(ScopeOverviewSelectors.selectCurrentScope)),
      filter(([{ sectionId }, currentScope]) => !!(currentScope.identity.id && sectionId)),
      mergeMap(([{ sectionId }, currentScope]) =>
        this.scopePaginatedService.getScopeSection(currentScope.identity.id, sectionId).pipe(
          map((section) => ScopePaginatedOverviewActions.getScopeSectionSuccess({ section })),
          catchError((error) => {
            this.snackbarService.showSnackbar(
              `An error occurred during loading the section`,
              SNACKBAR_LENGTH_LONG,
              SnackbarEventType.ERROR
            )
            return of(ScopePaginatedOverviewActions.getScopeSectionFail({ sectionId, error }))
          })
        )
      )
    )
  )

  triggerGetUpdatedScope$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ScopePaginatedOverviewActions.getScopeDeliverableSuccess,
        ScopeOverviewActions.updateScopeDeliverableSuccess,
        ScopeOverviewActions.deleteScopeDiscountSuccess,
        ScopeOverviewActions.updateScopeDiscountSuccess,
        ScopeOverviewActions.updateScopeTpcSuccess,
        ScopeOverviewActions.createScopeTpcSuccess,
        ScopeOverviewActions.deleteScopeTpcSuccess,
        ScopeOverviewActions.createScopeFeeItemSuccess,
        ScopeOverviewActions.editScopeFeeItemSuccess,
        ScopeOverviewActions.deleteScopeFeeItemSuccess,
        ScopeOverviewActions.createSectionFeeItemSuccess,
        ScopeOverviewActions.editSectionFeeItemSuccess,
        ScopeOverviewActions.deleteSectionFeeItemSuccess,
        ScopeOverviewActions.updateScopeMsaSuccess,
        ScopeOverviewActions.deleteScopeMsaSuccess,
        ScopeConfigurationActions.submitConfigurationSuccess
      ),
      filter(
        (action) =>
          !(action.type === ScopePaginatedOverviewActionTypes.GET_SCOPE_DELIVERABLE_SUCCESS && !action.isUpdated)
      ),
      withLatestFrom(this.store$.select(ScopeOverviewSelectors.selectCurrentScope)),
      map(([_, currentScope]) => currentScope.identity.id),
      filter((scopeId) => !!scopeId),
      map((scopeId) => ScopePaginatedOverviewActions.getCurrentScopeValues({ scopeId }))
    )
  )
}
